import _ from "lodash"
import moment from "moment"
import {decrypt} from "../../../utils/crypto"
import Errors from "../../../utils/Errors"
import {basicContext} from "../../../utils/contextUtils"
import {getButtonsForModule, calculateNetForecast, calculateReversionMaxAmount} from "./utils"
import {liberalityApplicationStatusObjects} from "../staticEntities"
import async from "async"

export const entities = [
    {
        name: 'LiberalityRecordStakeholder',
        fields: [
            {path: 'stakeholder', type: 'CUser'}
        ]
    },
    {
        name: 'AssetInfos',
        facets: ['conversations'],
        fields: [
            {path: 'typeOfProperty', type: 'TypeOfProperty', nullable: true},
            {path: 'identifier'},
            {path: 'comment'},

            {path: 'estimatedAssetValue', type: 'decimal', nullable: true},
            {path: 'assetLiabilities', type: 'decimal'},

            {path: 'netAmount', tKey: 'Net réalisé (€)', type: 'decimal', nullable: true},
            {path: 'achievedDate', tKey: 'Réalisé le', type: 'date', nullable: true},

            {
                path: 'estimatedAssetNetValue',
                fieldPath: ['estimatedAssetValue', 'assetLiabilities'],
                type: 'decimal',
                f: function() {
                    return (this.estimatedAssetValue ?? 0) - (this.assetLiabilities ?? 0)
                }
            },

            {
                path: 'beneficiariesShareInAssetEstimatedValue',
                type: 'decimal',
                fieldPath: ['distribution.beneficiaryShareInAssetEstimatedValue'],
                f: function() {
                    return _.sumBy(this.distribution || [], 'beneficiaryShareInAssetEstimatedValue')
                }
            },
            {
                path: 'beneficiariesShareInAssetLiabilities',
                type: 'decimal',
                fieldPath: ['distribution.beneficiaryShareInAssetLiabilities'],
                f: function() {
                    return _.sumBy(this.distribution || [], 'beneficiaryShareInAssetLiabilities')
                }
            },
            {
                path: 'beneficiariesOwnLiabilities',
                type: 'decimal',
                fieldPath: ['distribution.beneficiaryOwnLiabilities'],
                f: function() {
                    return _.sumBy(this.distribution || [], 'beneficiaryOwnLiabilities')
                }
            },
            {
                path: 'beneficiariesShareInLiberalityRecordActsFees',
                type: 'decimal',
                fieldPath: ['distribution.beneficiaryShareInLiberalityRecordActsFees'],
                f: function() {
                    return _.sumBy(this.distribution || [], 'beneficiaryShareInLiberalityRecordActsFees')
                }
            },

            {
                path: 'estimatedBeneficiariesNetValue',
                fieldPath: ['distribution.estimatedBeneficiaryNetValue'],
                type: 'decimal',
                f: function() {
                    return _.sumBy(this.distribution || [], 'estimatedBeneficiaryNetValue')
                }
            },
            {
                path: 'CNDAShareInEstimatedBeneficiariesNetValue',
                fieldPath: ['distribution.CNDAShareInEstimatedBeneficiaryNetValue'],
                type: 'decimal',
                f: function() {
                    return _.sumBy(this.distribution || [], 'CNDAShareInEstimatedBeneficiaryNetValue')
                }
            },

            {path: 'distribution', type: 'AssetPartition', link: 'OTM'},
            {path: 'status', type: 'AssetStatus', nullable: true},
            {path: 'filesList', type: 'FileList', link: {type: 'OTM', onParent: true, deleteType: 'cascade'}},
            {
                path: 'distributionHistory',
                //type: 'AssetDistributionHistory',
                //list: true,
                fieldPath: [
                    'distribution.distributionHistory.updatedAt',
                    'distribution.distributionHistory.updatedBy.name',
                    'distribution.distributionHistory.beneficiary.zipCodeAndName',
                    'distribution.distributionHistory.changedFields',
                    'distribution.distributionHistory.snapshot.beneficiaryOwnLiabilities',
                    'distribution.distributionHistory.snapshot.assetInfos.estimatedAssetValue',
                    'distribution.distributionHistory.snapshot.assetInfos.assetLiabilities',
                    'distribution.distributionHistory.snapshot.assetInfos.status.name'
                ],
                f: function() {
                    return _.flatMap(this.distribution || [], partition => partition.distributionHistory || [])
                }
            },
            {path: 'updatedBy', type: 'User', nullable: true},
            {path: 'updatedAt', type: 'date', dateFormat: 'YYYY-MM-DD HH:mm', nullable: true},
            {
                path: 'liberalityRecordDocumentsList',
                fieldPath: [
                    'liberalityRecord.filesList.id',
                    'liberalityRecord.filesList.fileCategory.id',
                    'liberalityRecord.filesList.fileCategory.title',
                    'liberalityRecord.filesList.filesNumber',
                    'liberalityRecord.filesList.files',
                ],
                f: function () {
                    return _.get(this, 'liberalityRecord.filesList') || []
                }
            },

            // getters derived from parent LiberalityRecord
            {
                path: 'applicationNumber',
                fieldPath: ['liberalityRecord.applicationNumber'],
                f: function () {
                    return _.get(this, 'liberalityRecord.applicationNumber')
                }
            },
            {
                path: 'administrator',
                fieldPath: ['liberalityRecord.administrator.id', 'liberalityRecord.administrator.fullName'],
                f: function () {
                    return _.get(this, 'liberalityRecord.administrator.fullName')
                }
            },
            {
                path: 'typeOfFile',
                fieldPath: ['liberalityRecord.typeOfFile.id', 'liberalityRecord.typeOfFile.fullName'],
                f: function () {
                    return _.get(this, 'liberalityRecord.typeOfFile.fullName')
                }
            },
            {
                path: 'benefactor',
                fieldPath: ['liberalityRecord.benefactor'],
                f: function () {
                    return _.get(this, 'liberalityRecord.benefactor')
                }
            },
            {
                path: 'typeOfPropertyCategory',
                fieldPath: ['typeOfProperty.name', 'typeOfProperty.category'],
                f: function () {
                    return _.get(this, 'typeOfProperty.category') ? _.get(this, 'typeOfProperty.name') : undefined
                }
            },
            {
                path: 'presentationDate',
                type: 'date',
                fieldPath: ['liberalityRecord.presentationDate'],
                f: function () {
                    return _.get(this, 'liberalityRecord.presentationDate')
                }
            },
            {
                path: 'beneficiaries',
                fieldPath: ['liberalityRecord.distribution.beneficiary.zipCodeAndName'],
                f: function () {
                    return (this.liberalityRecord?.distribution || []).map(share => _.get(share, 'beneficiary.zipCodeAndName')).join(', ')
                }
            },
            {
                path: 'fileStatus',
                //type: 'object',
                fieldPath: ['liberalityRecord.status.id'],
                f: function () {
                    return _.get(this, 'liberalityRecord.status')
                }
            },
            {
                path: 'fileDecisionDate',
                fieldPath: ['liberalityRecord.decisionDate'],
                f: function () {
                    return _.get(this, 'liberalityRecord.decisionDate') || ''
                }
            },
        ],
        beforeSave: (object, oldObject, context, callback) => {
            object.updatedBy = _.pick(context.user, ['id', 'name'])
            object.updatedAt = new Date()
            callback(null, object, oldObject)
        },
        afterSave: (object, oldObject, context, callback) => {
            const assetInfos = object //_.pick(object, ['estimatedAssetValue', 'assetLiabilities', 'status'])
            let oldAssetInfos = oldObject //&& _.pick(oldObject, ['estimatedAssetValue', 'assetLiabilities', 'status'])

            async.each(object.distribution || [], (partition, cb) => {
                let oldPartition = (context.businessOldObject?.distribution || []).find(p => new global.ObjectID(p.id).toString() === new global.ObjectID(partition.id).toString())
                if (context.module.name === 'LiberalityRecord') {
                    oldAssetInfos = (context.businessOldObject?.assetsInfos || []).find(asset => new global.ObjectID(asset.id).toString() === new global.ObjectID(object.id).toString())
                    oldPartition = (oldAssetInfos?.distribution || []).find(p => new global.ObjectID(p.id).toString() === new global.ObjectID(partition.id).toString())
                }
                global.app.C.AssetPartition.afterSave(
                    {...partition, assetInfos},
                    {...oldPartition, assetInfos: oldAssetInfos},
                    context,
                    cb
                )
            }, err => callback(err, object, oldObject))
        },
        filters: [
            {
                name: 'byLiberalityRecord',
                isDefault: false,
                async: true,
                query: async function (context, callback) {
                    const liberalityRecordId = _.get(context, "data.liberalityRecordId")
                    if (!liberalityRecordId) return callback(null, {_id: null})

                    return callback(null, { liberalityRecord: new global.ObjectID(liberalityRecordId) })
                }
            },
            {
                name: 'byLiberalityRecordAndBeneficiary',
                isDefault: false,
                async: true,
                query: async function (context, callback) {
                    const liberalityRecordId = _.get(context, "data.liberalityRecordId")
                    if (!liberalityRecordId) return callback(null, {_id: null})

                    const beneficiaryId = _.get(context, "data.beneficiaryId")
                    if (!beneficiaryId) {
                        return callback(null, { liberalityRecord: new global.ObjectID(liberalityRecordId) })
                    }

                    const partitions = await global.app.C.AssetPartition.find({
                        ...basicContext(context),
                        query: {beneficiary: new global.ObjectID(beneficiaryId)},
                        fieldPath: ['assetInfos.id']
                    })
                    const assetIds = [...new Set(partitions.map(p => p.assetInfos?.id).filter(Boolean))]

                    return callback(null, {
                        liberalityRecord: new global.ObjectID(liberalityRecordId),
                        _id: {$in: assetIds.map(id => new global.ObjectID(id))}
                    })
                }
            },
            {
                name: 'byAsset',
                isDefault: false,
                async: true,
                query: async function (context, callback) {
                    const assetId = _.get(context, "data.assetId")
                    if (!assetId) return callback(null, {_id: null})

                    return callback(null, { _id: new global.ObjectID(assetId) })
                }
            },
            {
                name: 'byStatus',
                title: 'Status',
                type: "tags",
                path: 'status',
                object: 'LiberalityApplicationStatus',
                display: 'name',
                default: [{id: 'draft'}, {id: 'incomplete'}],
                client: true,
                isDefault: false,
                timer: 3,
                async: true,
                query: async function(context, callback) {
                    const statuses = _.get(context, "data.status") || []
                    if (!statuses || statuses.length === 0) return callback(null, {})

                    const liberalityRecords = await global.app.C.LiberalityRecord.find({
                        fieldPath: ['id'],
                        ...basicContext(context),
                        query: {status: {$in: statuses.map(o => o.id)}}
                    })

                    return callback(null, {liberalityRecord: {$in: liberalityRecords.map(f => new global.ObjectID(f.id))}})
                }
            }
        ]
    },
    {
        name: 'AssetDistributionHistory',
        fields: [
            {path: 'beneficiary', type: 'Organization'},
            {path: 'updatedBy', type: 'User'},
            {path: 'updatedAt', type: 'date', dateFormat: 'YYYY-MM-DD HH:mm:ss'},
            {path: 'changedFields', list: true, type: 'object'},
            {path: 'snapshot', type: 'object'},
        ]
    },
    {
        name: 'AssetPartition',
        fields: [
            {path: 'beneficiary', type: 'Organization'},
            {path: 'confFor', type: 'boolean'},
            {type: 'Qualification', nullable: true},
            {path: 'beneficiaryOwnLiabilities', type: 'decimal', nullable: true},
            {path: 'actsFeesSupported', type: 'boolean'},
            {path: 'share', type: 'decimal', tKey: 'Quote-part'},

            {
                path: 'beneficiaryShareInAssetEstimatedValue',
                type: 'decimal',
                fieldPath: ['assetInfos.estimatedAssetValue', 'share'],
                f: function() {
                    return (this.assetInfos?.estimatedAssetValue ?? 0) * (this.share ?? 0) / 100
                }
            },
            {
                path: 'beneficiaryShareInAssetLiabilities',
                type: 'decimal',
                fieldPath: ['assetInfos.assetLiabilities', 'share'],
                f: function() {
                    return (this.assetInfos?.assetLiabilities ?? 0) * (this.share ?? 0) / 100
                }
            },
            {
                path: 'beneficiaryShareInLiberalityRecordActsFees',
                type: 'decimal',
                fieldPath: ['actsFeesSupported', 'assetInfos.liberalityRecord.actsFees', 'beneficiaryShareInAssetEstimatedValue', 'assetInfos.liberalityRecord.assetsInfos.distribution.actsFeesSupported', 'assetInfos.liberalityRecord.assetsInfos.distribution.beneficiaryShareInAssetEstimatedValue'],
                f: function() {
                    if (!this.actsFeesSupported) return 0
                    const partitions = _.flatMap(this.assetInfos?.liberalityRecord?.assetsInfos || [], assetInfo => assetInfo.distribution || [])
                    const totalValue = _.sumBy(partitions.filter(partition => partition.actsFeesSupported), 'beneficiaryShareInAssetEstimatedValue')
                    if (!totalValue) return 0
                    return (this.assetInfos?.liberalityRecord?.actsFees ?? 0) * ((this.beneficiaryShareInAssetEstimatedValue ?? 0) / totalValue)
                }
            },

            {
                path: 'estimatedBeneficiaryNetValue',
                fieldPath: ['assetInfos.estimatedAssetValue', 'assetInfos.assetLiabilities', 'share', 'beneficiaryOwnLiabilities', 'beneficiaryShareInLiberalityRecordActsFees'],
                type: 'decimal',
                f: function() {
                    const estimatedAssetNetValue = (this.assetInfos?.estimatedAssetValue ?? 0) - (this.assetInfos?.assetLiabilities ?? 0)
                    return estimatedAssetNetValue * (this.share ?? 0) / 100 - (this.beneficiaryOwnLiabilities ?? 0) - (this.beneficiaryShareInLiberalityRecordActsFees ?? 0)
                }
            },
            {
                path: 'CNDAShareInEstimatedBeneficiaryNetValue',
                fieldPath: ['assetInfos.estimatedAssetValue', 'assetInfos.assetLiabilities', 'share', 'beneficiaryShareInLiberalityRecordActsFees'],
                type: 'decimal',
                f: function () {
                    const estimatedAssetNetValue = (this.assetInfos?.estimatedAssetValue ?? 0) - (this.assetInfos?.assetLiabilities ?? 0)
                    const partToInclude = estimatedAssetNetValue * (this.share ?? 0) / 100 - (this.beneficiaryShareInLiberalityRecordActsFees ?? 0)
                    return (partToInclude * 6) / 100
                }
            },
            {path: 'updatedBy', type: 'User', nullable: true},
            {path: 'updatedAt', type: 'date', dateFormat: 'YYYY-MM-DD HH:mm', nullable: true},
            {path: 'distributionHistory', type: 'AssetDistributionHistory', link: {type: 'OTM', onParent: false, onChild: true, deleteType: 'cascade'}}
        ],
        beforeSave: (object, oldObject, context, callback) => {
            object.updatedBy = _.pick(context.user, ['id', 'name'])
            object.updatedAt = new Date()
            callback(null, object, oldObject)
        },
        afterSave: (object, oldObject, context, callback) => {
            const trackedFields = [
                'beneficiaryOwnLiabilities',
                'assetInfos.estimatedAssetValue',
                'assetInfos.assetLiabilities',
                'assetInfos.status',
            ]

            const objectToCompare = {
                ..._.pick(object, ['id', 'beneficiaryOwnLiabilities']),
                assetInfos: {
                    ..._.pick(object.assetInfos, ['id', 'estimatedAssetValue', 'assetLiabilities']),
                    status: _.pick(object.assetInfos?.status, ['id', 'name'])
                }
            }

            const oldObjectToCompare = {
                ..._.pick(oldObject, ['id', 'beneficiaryOwnLiabilities']),
                assetInfos: {
                    ..._.pick(oldObject.assetInfos, ['id', 'estimatedAssetValue', 'assetLiabilities']),
                    status: _.pick(oldObject.assetInfos?.status, ['id', 'name'])
                }
            }

            const changedFields = !oldObject
                ? trackedFields
                : trackedFields.filter(field => !_.isEqual(_.get(objectToCompare, field), _.get(oldObjectToCompare, field)))

            if (changedFields.length === 0) return callback(null, object, oldObject)

            const snapshot = {
                ..._.pick(object, ['id', 'beneficiaryOwnLiabilities']),
                assetInfos: {
                    ..._.pick(object.assetInfos, ['id', 'estimatedAssetValue', 'assetLiabilities']),
                    status: _.pick(object.assetInfos?.status, ['id', 'name'])
                }
            }

            global.app.C.AssetDistributionHistory.collection.insertOne({
                assetPartition: new global.ObjectID(object.id),
                beneficiary: new global.ObjectID(object.beneficiary.id),
                updatedBy: new global.ObjectID(context.user.id),
                updatedAt: new Date(),
                changedFields: changedFields.map(field => ({id: field, name: field.split('.').pop()})),
                snapshot,
                group: new global.ObjectID(context.group.id)
            }).then(() => callback(null, object, oldObject)).catch(err => callback(err, object, oldObject))
        },
        filters: [
            {
                name: 'byBeneficiary',
                title: 'Bénéficiaire',
                path: 'beneficiary',
                object: 'Organization',
                display: 'zipCodeAndName',
                client: true,
                isDefault: false,
                filters: ['isAssociation', 'inAssetPartition'],
                query: context => {
                    const beneficiary = _.get(context, "data.beneficiary")
                    if (!beneficiary?.id) return {}
                    return {beneficiary: new global.ObjectID(beneficiary.id)}
                }
            }
        ]
    },
    {
        name: 'LiberalityRecord',
        facets: ['files', 'conversations'],
        fields: [
            // temporary
            {path: 'beneficiary', type: 'Organization', nullable: true},
            {path: 'stakeholders', type: 'LiberalityRecordStakeholder', link: 'OTM'},
            {path: 'assetsInfos', type: 'AssetInfos', link: 'OTM'},
            {path: 'receivables', type: 'LiberalityRecordReceivable', link: 'OTM'},
            {path: 'entries', type: 'LiberalityReversionEntry', link: 'OTM'},
            {path: 'departures', type: 'LiberalityReversionDeparture', link: 'OTM'},
            {path: 'applicationNumber', type: 'string', unique: true},
            {
                path: "sequence",
                unique: true,
                ps: {
                    object: [{
                        type: "nextSequence",
                        sequenceId: "liberalityRecordApplicationSequence",
                        formatResult: function(result) {
                            return result.toString().padStart(4, '0') // Formats the sequence number, e.g., "0001"
                        }
                    }]
                }
            },

            /* Parties */
            {path: 'administrator', type: 'CUser'},
            {path: 'typeOfFile', type: 'TypeOfLiberalityRecord'},
            {
                path: 'zipCode',
                fieldPath: ['beneficiary.zipCode', 'lifeInsuranceNetAmount'],
                f: function () {
                    return this.beneficiary?.zipCode
                }
            },
            {
                path: 'benefactor',
                fieldPath: ['benefactorFirstName', 'benefactorBirthName'],
                f: function () {
                    return `${this.benefactorFirstName} ${this.benefactorBirthName}`
                }
            },
            {path: 'benefactorFirstName'},
            {path: 'benefactorBirthName'},
            {path: 'benefactorMaritalStatus', type: 'LiberalityMaritalStatus', nullable: true},
            {path: 'benefactorMaritalName', nullable: true},
            {path: 'benefactorBirthDate', type: 'date', nullable: true},
            {path: 'benefactorBirthPlace', nullable: true},
            {path: 'benefactorDeathDate', type: 'date', nullable: true},
            {path: 'benefactorDeathPlace', nullable: true},
            {path: 'benefactorMainResidence', nullable: true},
            {path: 'benefactorReservatoryHeirs', type: 'boolean'},
            {path: 'comment'},

            {
                path: 'estimatedLiberalityRecordValue',
                type: 'decimal',
                fieldPath: ['assetsInfos.beneficiariesShareInAssetEstimatedValue'],
                f: function() {
                    return _.sumBy(this.assetsInfos || [], 'beneficiariesShareInAssetEstimatedValue')
                }
            },
            {path: 'estimatedLiberalityRecordValueDetails'},

            {
                path: 'liberalityRecordLiabilities',
                type: 'decimal',
                fieldPath: ['assetsInfos.beneficiariesShareInAssetLiabilities'],
                f: function() {
                    return _.sumBy(this.assetsInfos || [], 'beneficiariesShareInAssetLiabilities')
                }
            },
            {path: 'liberalityRecordLiabilitiesDetails'},

            {
                path: 'liberalityRecordOwnLiabilities',
                type: 'decimal',
                fieldPath: ['assetsInfos.beneficiariesOwnLiabilities'],
                f: function() {
                    return _.sumBy(this.assetsInfos || [], 'beneficiariesOwnLiabilities')
                }
            },

            {
                path: 'liberalityRecordBeneficiariesActsFees',
                type: 'decimal',
                fieldPath: ['assetsInfos.beneficiariesShareInLiberalityRecordActsFees'],
                f: function() {
                    return _.sumBy(this.assetsInfos || [], 'beneficiariesShareInLiberalityRecordActsFees')
                }
            },

            {path: 'actsFees', type: 'decimal'},
            {path: 'actsFeesDetails'},

            {
                path: 'netForecast',
                fieldPath: ['estimatedLiberalityRecordValue', 'liberalityRecordLiabilities', 'liberalityRecordOwnLiabilities', 'actsFees', 'liberalityRecordBeneficiariesActsFees'],
                type: 'decimal',
                f: function() {
                    const result = (this.estimatedLiberalityRecordValue ?? 0) - (this.liberalityRecordLiabilities ?? 0) - (this.liberalityRecordOwnLiabilities ?? 0) - (this.liberalityRecordBeneficiariesActsFees ?? 0)
                    return result
                }
            },
            {
                path: 'CNDAShare',
                fieldPath: ['assetsInfos.CNDAShareInEstimatedBeneficiariesNetValue'],
                type: 'decimal',
                f: function () {
                    return _.sumBy(this.assetsInfos || [], 'CNDAShareInEstimatedBeneficiariesNetValue')
                }
            },
            {
                path: 'qualificationsDisplay',
                fieldPath: ['assetsInfos.distribution.qualification.name'],
                f: function () {
                    const distributions = _.flatMap(this.assetsInfos || [], a => a.distribution || [])
                    return [...new Set(distributions.map(item => _.get(item, 'qualification.name')).filter(Boolean))].join(', ')
                }
            },
            {
                path: 'beneficiariesDisplay',
                fieldPath: ['assetsInfos.distribution.beneficiary.zipCodeAndName'],
                f: function () {
                    const distributions = _.flatMap(this.assetsInfos || [], a => a.distribution || [])
                    return [...new Set(distributions.map(item => _.get(item, 'beneficiary.zipCodeAndName')).filter(Boolean))].join(', ')
                }
            },

            {
                path: 'fileApplicationNumberAndBenefactor',
                fieldPath: ['applicationNumber', 'benefactor'],
                f: function () {
                    return this.applicationNumber + ' : ' + this.benefactor
                }
            },

            {
                path: 'beneficiaries',
                fieldPath: ['assetsInfos.distribution.beneficiary.zipCodeAndName'],
                f: function () {
                    const distributions = _.flatMap(this.assetsInfos || [], a => a.distribution || [])
                    return distributions.map(share => _.get(share, 'beneficiary.zipCodeAndName')).join(', ')
                }
            },

            {
                path: 'CNDABeneficiaries',
                fieldPath: ['distribution.beneficiary.zipCodeAndName'],
                f: function () {
                    const distributions = _.flatMap(this.assetsInfos || [], a => a.distribution || [])
                    return distributions.filter(share => share.confFor)
                        .map(share => _.get(share, 'beneficiary.zipCodeAndName')).join(', ')
                }
            },
            {
                path: 'totalAmount',
                type: 'decimal',
                fieldPath: [
                    //'netAssets',
                    'netForecast',
                    'lifeInsuranceNetAmount'],
                f: function () {
                    //const netAssets = this.netAssets || 0
                    const netForecast = this.netForecast || 0
                    const lifeInsuranceNetAmount = this.lifeInsuranceNetAmount || 0
                    return netForecast + lifeInsuranceNetAmount
                }
            },

            {path: 'status', type: 'LiberalityApplicationStatus', nullable: true},
            {
                path: 'statusName',
                fieldPath: ['status.id'],
                f: function () {
                    const statusObject = liberalityApplicationStatusObjects.find(status => status.id === this.status.id)
                    return statusObject?.name
                }
            },

            {path: 'createdAt', type: 'date', nullable: true},
            {path: 'updatedAt', type: 'date', dateFormat: 'YYYY-MM-DD HH:mm', nullable: true},
            {path: 'updatedBy', type: 'User', nullable: true},
            {path: 'decisionDate', type: 'date', dateFormat: 'YYYY-MM-DD HH:mm', nullable: true},
            {
                path: "presentationDate",
                fieldPath: ['id'],
                $f: function (file, context, callback) {
                    global.app.C.LiberalityRecordPresentation.collection
                        .find({liberalityRecords: {$elemMatch: {$eq: new global.ObjectID(file.id)}}})
                        .sort({ presentationDate: -1 })
                        .limit(1)
                        .toArray()
                        .then(presentations => {
                            const presentation = presentations[0]
                            if(!presentation) return callback(null, '')
                            callback(null, presentation.presentationDate)
                        }).catch(callback)
                }
            },
            {
                path: "buttons",
                fieldPath: ['status.id'],
                $f: function (file, context, callback) {
                    const buttons = getButtonsForModule(file, context);
                    callback(null, buttons);
                }
            },
            {
                path: "noDeleteButtonAccess",
                fieldPath: ['status.id'],
                f: function () {
                    const statusId = _.get(this, 'status.id')
                    return !['draft', 'incomplete', 'readyToBePresented', 'updated'].includes(statusId)
                }
            },
            {
                path: 'netForecastTotal',
                tKey: 'Net prévu (€)',
                type: 'decimal',
                nullable: true,
                fieldPath: ['assetsInfos.estimatedAssetValue', 'assetsInfos.assetLiabilities'],
                f: function () {
                    return _.sumBy(this.assetsInfos || [], asset => (asset.estimatedAssetValue ?? 0) - (asset.assetLiabilities ?? 0))
                }
            },
            {
                path: 'netAchievedTotal',
                tKey: 'Net réalisé (€)',
                type: 'decimal',
                nullable: true,
                fieldPath: ['assetsInfos.netAmount'],
                f: function () {
                    return _.sumBy(this.assetsInfos || [], 'netAmount')
                }
            },
            {
                path: 'receivedTotal',
                tKey: 'Reçu (€)',
                type: 'decimal',
                nullable: true,
                fieldPath: ['entries.receivedAmount'],
                f: function () {
                    return _.sumBy(this.entries || [], 'receivedAmount')
                }
            },
            {
                path: 'reversedTotal',
                tKey: 'Reversé (€)',
                type: 'decimal',
                nullable: true,
                fieldPath: ['departures.departureForm.id', 'departures.reversionAmount'],
                f: function () {
                    return _.sumBy((this.departures || []).filter(d => d.departureForm?.id === 'reversion'), 'reversionAmount')
                }
            },
            {
                path: 'advanceRecoveryTotal',
                tKey: "Reprise d'avance (€)",
                type: 'decimal',
                nullable: true,
                fieldPath: ['departures.departureForm.id', 'departures.advanceRecoveryAmount'],
                f: function () {
                    return _.sumBy((this.departures || []).filter(d => d.departureForm?.id === 'advanceRecovery'),
                        'advanceRecoveryAmount')
                }
            },
            {
                path: 'CNDAShareTotal',
                tKey: 'Part CNDA (€)',
                type: 'decimal',
                nullable: true,
                fieldPath: ['entries.CNDAShare'],
                f: function () {
                    return _.sumBy(this.entries || [], 'CNDAShare')
                }
            },

            {path: 'filesList', type: 'FileList', link: {type: 'OTM', onParent: true, deleteType: 'cascade'}},
        ],
        filters: [
            {
                name: 'byStatus',
                title: 'Status',
                type: "tags",
                path: 'status',
                object: 'LiberalityApplicationStatus',
                display: 'name',
                default: [{id: 'draft'}, {id: 'incomplete'}],
                client: true,
                isDefault: false,
                timer: 3,
                query: context => {
                    const statuses = _.get(context , "data.status");

                    if(!statuses || statuses.length === 0) return {}
                    return {status: {$in: statuses.map(o => o.id)}};
                }
            },
            {
                name: "readyTobePresented",
                isDefault: false,
                query: () => {
                    return {status: {$in: ['readyToBePresented', 'updated']}}
                }
            },
            {
                name: "accepted",
                isDefault: false,
                query: () => {
                    return {status: 'validated'}
                }
            },
            {
                name: "notUsedInPresentation",
                isDefault: false,
                async: true,
                query: async (context, callback) => {
                    const presentations = await global.app.C.LiberalityRecordPresentation.find({
                        ...basicContext(context),
                        fieldPath: [],
                        query: {}
                    });

                    const usedFilesIds = _.flatMap(presentations, presentation => presentation.liberalityRecords.map(id => new global.ObjectID(id)));

                    return callback(null, {_id: {$nin: usedFilesIds}})
                }
            },
            {
                name: "inPresentationBeforeOrToday",
                isDefault: false,
                async: true,
                query: async (context, callback) => {
                    const today = moment().format('YYYY-MM-DD');

                    const presentations = await global.app.C.LiberalityRecordPresentation.find({
                        ...basicContext(context),
                        fieldPath: [],
                        query: { presentationDate: { $lte: today } }
                    })

                    const fileIds = _.flatMap(presentations, presentation =>
                        presentation.liberalityRecords.map(id => new global.ObjectID(id))
                    );

                    return callback(null, { _id: { $in: fileIds } });
                }
            }
            /*
            {
                name: "byUser",
                isDefault: false,
                async: true,
                query: (context, callback) => {
                    global.app.C.CUser.collection.findOne({kpUser: new global.ObjectID(context.user.id)}, (e, cUser) => {
                        if(!cUser) return callback(null, {_id: null})
                        return callback(null, {mail: cUser.mail})
                    })
                }
            }
             */
        ],
        ps: {
            context: [{
                $$u: function (context, callback) {
                    if (this.options.accessType === "S" && context.restAction && context.restAction.crudType === "C") {
                        context.internalFieldPath = [
                            ...new Set([
                                ...context.internalFieldPath,
                                "sequence" // Ensures 'sequence' is included for sequence generation on creation
                            ])
                        ];
                    }
                    callback(null, context)
                }
            }]
        },
        requiredAuthorization: async (context, callback) => {
            const moduleId = context.clientContext.moduleId
            if(moduleId === 'm-C-supportModule') return callback()
            const functions = await global.app.C.Function.find({
                ...basicContext(context),
                query: {},
                fieldPath: ['id'],
                filters: ['liberalityProcessAdministrator']
            })

            const cUser = await global.app.C.CUser.collection.findOne({
                kpUser: new global.ObjectID(context.user.id),
                functions: {$elemMatch: {$in: functions.map(func => new global.ObjectID(func.id))}}
            })
            if(!cUser) return callback(new Errors.ValidationError("Autorisation requise manquante"))
            return callback()
        },
        duplicateBeneficiaries: (object, context, callback) => {
            const beneficiaryIds = object.distribution.map(item => item.beneficiary?.id)

            const seen = new Set()
            const duplicates = beneficiaryIds.filter(id => {
                if (seen.has(id)) {
                    return true
                }
                seen.add(id);
                return false
            })

            if (duplicates.length > 0) {
                return callback(new Errors.ValidationError('Bénéficiaires en double trouvés'))
            }

            return callback()
        },
        validateTotalShares: (object, context, callback) => {
            if(!object.distribution.length) return callback(new Errors.ValidationError('La saisie de du champs répartition est obligatoire'));
            const totalShare = object.distribution.reduce((total, item) => {
                return total + (item.share || 0);
            }, 0)

            if (totalShare > 100) {
                return callback(new Errors.ValidationError(`La somme des parts doit être inférieur ou égale à 100, actuellement : ${totalShare}`));
            }

            return callback()
        },
        presentationDateRequirement: (object, context, callback) => {
            const moduleId = context.clientContext.moduleId

            if(moduleId === 'm-C-liberalityDecision' && object.presentationDate > moment().format('YYYY-MM-DD')) {
                return callback(new Errors.ValidationError("Vous ne pouvez pas appliquer une décision pour un dossier dont la date de présentation est postérieure à la date du jour"))
            }
            return callback()
        },
        validateSave: async function(object, oldObject, context, callback){
            async.series([
                callback => this.requiredAuthorization(context, callback),
                //callback => this.duplicateBeneficiaries(object, context, callback),
                //callback => this.validateTotalShares(object, context, callback),
                callback => this.presentationDateRequirement(object, context, callback),
            ], callback)
        },
        beforeSave: (object, oldObject, context, callback) => {
            object.updatedAt = new Date()
            object.updatedBy = _.pick(context.user, ['id', 'name'])

            if (context.restAction && context.restAction.crudType === "C") {

                const currentYear = new Date().getFullYear()
                object.applicationNumber = `LIB-${currentYear}-${object.sequence}`
                object.createdAt =  new Date()
                object.status = {id: 'draft'}

                object.conversations.push(
                    {
                        text: "Brouillon",
                        sender: _.pick(context.user, ['id', 'name']),
                        seenBy: [context.user.id],
                        files: [],
                        timestamp: new Date().toISOString(),
                        from: 'system'
                    }
                )
                return callback(null, object, oldObject)
            }

            object.lastComment = _.last(_.orderBy(object.conversations, 'timestamp')).text

            const action = context.action
            if(action === 'save') return callback(null, object, oldObject)
            const actionButton = object.buttons.find(button => button.action === action)
            object.status = {id: actionButton.nextStatus}
            const statusObject = liberalityApplicationStatusObjects.find(status => status.id === object.status.id)
            const moduleId = context.clientContext.moduleId
            const isTakingADecision = ['validate', 'reject', 'adjourn'].includes(action) && moduleId === 'm-C-liberalityDecision'
            if (isTakingADecision) object.decisionDate = new Date()
            object.conversations.push({
                text: statusObject.name,
                sender: _.pick(context.user, ['id', 'name']),
                seenBy: [context.user.id],
                files: [],
                timestamp: new Date().toISOString(),
                from: 'system'
            })
            return callback(null, object, oldObject)
        },
        afterSave: (object, oldObject, context, callback) => {
            if (context.module?.name !== 'LiberalityRecord') return callback(null, object, oldObject)

            async.each(object.assetsInfos || [], (assetInfos, cb) => {
                const oldAssetInfos = (context?.businessOldObject?.assetsInfos || []).find(a => a.id === assetInfos.id)
                global.app.C.AssetInfos.afterSave(assetInfos, oldAssetInfos, context, cb)
            }, err => callback(err, object, oldObject))
        },
    },
    {
        name: 'LiberalityRecordReceivableImputation',
        type: 'mongoInternal',
        fields: [
            {path: 'asset', type: 'AssetInfos'},
            {path: 'beneficiary', type: 'Organization'},
            {path: 'imputedAmount', type: 'decimal', default: 0}
        ]
    },
    {
        name: 'LiberalityRecordReceivable',
        facets: ['files'],
        fields: [
            {path: 'pieceNumber', nullable: false},
            {path: 'pieceDate', type: 'date'},
            {path: 'creditor', type: 'Organization'},
            {path: 'expenseCategory', type: 'LiberalityExpenseCategory'},
            {path: 'totalAmount', type: 'decimal'},
            {path: 'dueDate', type: 'date', nullable: true},
            {path: 'imputations', type: 'LiberalityRecordReceivableImputation', link: 'OTM'},
            {path: 'status', type: 'LiberalityReceivableStatus', nullable: true},
            {path: 'settlementDate', type: 'date', nullable: true},
            {path: 'comment', nullable: true}
        ]
    },
    {
        name: 'LiberalityReversionEntry',
        fields: [
            {path: 'asset', type: 'AssetInfos'},
            {path: 'date', type: 'date'},
            {path: 'receivedAmount', tKey: 'Reçu (€)', type: 'decimal'},
            {path: 'reason', tKey: 'Motif', type: 'LiberalityReversionEntryReason'},
            {path: 'comment', nullable: true},
            {path: 'CNDAShare', tKey: 'Part CNDA (€)', type: 'decimal', default: 0},
        ],
    },
    {
        name: 'LiberalityReversionDeparture',
        fields: [
            {path: 'asset', type: 'AssetInfos'},
            {path: 'date', type: 'date'},
            {path: 'beneficiary', type: 'Organization'},
            {path: 'departureForm', tKey: 'Forme', type: 'LiberalityReversionDepartureForm'},
            {path: 'reversionAmount', tKey: 'Reversion (€)', type: 'decimal', nullable: true},
            {path: 'advanceRecoveryAmount', tKey: "Reprise d'avance (€)", type: 'decimal', nullable: true},
            {path: 'comment', nullable: true},
            {path: 'status', tKey: 'Statut', type: 'LiberalityReversionDepartureStatus'},
            {path: 'executedDate', tKey: 'Exécuté le', type: 'date', nullable: true},

            {
                path: 'advance',
                type: 'LiberalityAdvance',
                nullable: true,
                fieldPath: ['beneficiary.id'],
                $f: function (departure, context, callback) {
                    if (!departure.beneficiary) return callback(null, null)
                    global.app.C.LiberalityAdvance.find({
                        ...basicContext(context),
                        query: {beneficiary: new global.ObjectID(departure.beneficiary.id)},
                        fieldPath: ['grantedTotal', 'imputedTotal']
                    }).then(advances => callback(null, advances[0] || null)).catch(callback)
                }
            },

            {
                path: 'reversionMaxAmount',
                tKey: 'Reversion maximum (€)',
                type: 'decimal',
                fieldPath: [
                    'beneficiary.id', 'asset.id', 'id',
                    'liberalityRecord.entries.receivedAmount', 'liberalityRecord.entries.asset.id',
                    'liberalityRecord.entries.CNDAShare',
                    'liberalityRecord.departures.id', 'liberalityRecord.departures.departureForm.id',
                    'liberalityRecord.departures.reversionAmount', 'liberalityRecord.departures.advanceRecoveryAmount',
                    'liberalityRecord.departures.beneficiary.id', 'liberalityRecord.departures.asset.id',
                    'liberalityRecord.liberalityRecord.assetsInfos.id',
                    'liberalityRecord.liberalityRecord.assetsInfos.distribution.share',
                    'liberalityRecord.liberalityRecord.assetsInfos.distribution.beneficiary.id',
                    'liberalityRecord.liberalityRecord.receivables.imputations.beneficiary.id',
                    'liberalityRecord.liberalityRecord.receivables.imputations.asset.id',
                    'liberalityRecord.liberalityRecord.receivables.imputations.imputedAmount',
                    'liberalityRecord.liberalityRecord.receivables.status.id',
                ],
                f: function () {
                    return calculateReversionMaxAmount(this)
                }
            },

            {
                path: 'advanceRecoveryMaxAmount',
                tKey: "Reprise d'avance (€) maximum (€)",
                type: 'decimal',
                fieldPath: ['advance.grantedTotal', 'advance.imputedTotal'],
                f: function () {
                    return this.advance ? (this.advance.grantedTotal - this.advance.imputedTotal) : 0
                }
            }
        ],
    }
]
